import { NotFoundPage } from '@components/NotFound/NotFoundPage';
import SharePhotoRecoveryPage from '@components/PhotolabQuiz/components/SharePhotoRecoveryPage/SharePhotoRecoveryPage';
import { useCheckAuth } from '@hooks/useCheckAuth';
import { getLastPath } from '@utils/ulr';
import React, { Suspense } from 'react';
import { Route, Routes as RoutesInner } from 'react-router-dom';

import { Routes } from './App';

const Main = React.lazy(() => import('@components/Main'));
const MydnaOnboarding = React.lazy(() => import('@components/features/MydnaOnboarding/MydnaOnboarding'));
const SubscriptionV2 = React.lazy(() => import('@components/SubscriptionV2'));
const OffersV2 = React.lazy(() => import('@components/OffersV2/OffersV2'));
const AccessProduct = React.lazy(() => import('@components/AccessProduct'));
const SharePage = React.lazy(() => import('@components/SharePage'));
const SignInV2 = React.lazy(() => import('@components/SignInV2/SignInV2'));
const Quiz = React.lazy(() => import('@components/Quiz/Quiz'));
const PhotolabQuiz = React.lazy(
  () => import('@components/PhotolabQuiz/PhotolabQuiz'),
);
const EthnicityQuiz = React.lazy(
  () => import('@components/EthnicityQuiz/EthnicityQuiz'),
);
const ComparisonQuiz = React.lazy(
  () => import('@components/ComparisonQuiz/ComparisonQuiz'),
);
const LookalikeQuiz = React.lazy(
  () => import('@components/LookalikeQuiz/LookalikeQuiz'),
);
const LookalikeReportHistoryWrap = React.lazy(
  () =>
    import(
      '@components/LookalikeQuiz/components/LookalikeReportHistory/LookalikeReportHistoryWrap'
    ),
);
const WebApp = React.lazy(() => import('@components/WebApp'));
const Unsubscribe = React.lazy(
  () => import('@components/Unsubscribe/Unsubscribe'),
);
const UnsubscribeWithEmail = React.lazy(
  () => import('@components/UnsubscribeWithEmail/UnsubscribeWithEmail'),
);
const AccessRecovery = React.lazy(
  () => import('@components/AccessRecovery/AccessRecovery'),
);
const ManageSubscriptionsV2 = React.lazy(
  () => import('@components/WebApp/pages/v2/ManageSubscriptionsV2'),
);
const Profile = React.lazy(() => import('@components/WebApp/pages/Profile'));
const ReportsHistory = React.lazy(
  () => import('@components/WebApp/pages/ReportsHistory'),
);
const FamilyTree = React.lazy(
  () => import('@components/WebApp/pages/FamilyTree'),
);

const FaceMatchHistory = React.lazy(
  () => import('@components/WebApp/pages/FaceCompare'),
);

const CelebrityLookalikeHistory = React.lazy(
  () =>
    import(
      '@components/WebApp/pages/CelebrityLookalikeHistory/CelebrityLookalikeHistory'
    ),
);
const GeneralReportsHistory = React.lazy(
  () =>
    import(
      '@components/WebApp/pages/GeneralReportsHistory/GeneralReportsHistory'
    ),
);
const MyEthnicityWrap = React.lazy(
  () => import('@components/MyEthnicity/MyEthnicityWrap'),
);
const WebAppMainV2 = React.lazy(
  () => import('@components/WebApp/pages/v2/WebAppMainV2'),
);
const PhotoLab = React.lazy(
  () => import('@components/WebApp/pages/v2/PhotoLab'),
);
const PhotoRecovery = React.lazy(
  () => import('@components/WebApp/pages/v2/PhotoRecovery'),
);
const PhotoRecoveryResults = React.lazy(
  () =>
    import(
      '@components/WebApp/pages/v2/PhotoRecovery/components/PhotoRecoveryResults'
    ),
);
const PhotoAnimation = React.lazy(
  () => import('@components/WebApp/pages/v2/PhotoAnimation'),
);
const PhotoAnimationResults = React.lazy(
  () =>
    import(
      '@components/WebApp/pages/v2/PhotoAnimation/components/PhotoAnimationResults'
    ),
);
const ComparisionReports = React.lazy(
  () => import('@components/ComparisonReport/ComparisonReportPage'),
);
const LookalikeReports = React.lazy(
  () =>
    import(
      '@components/LookalikeQuiz/components/LookalikeShareReportPage/LookalikeShareReportPage'
    ),
);
const WebAppPreview = React.lazy(
  () => import('@components/WebAppPreview/WebAppPreview'),
);

export default function Router() {
  useCheckAuth();

  return (
    <Suspense fallback={<div />}>
      <RoutesInner>
        <Route path="/onboarding/*" element={(typeof window !== 'undefined' && window.location.host.includes('mydnatest24.com')) ? <MydnaOnboarding /> : <Main />} />
          <Route path={Routes.MAIN + '/*'} element={(typeof window !== 'undefined' && window.location.host.includes('mydnatest24.com')) ? <MydnaOnboarding /> : <Main />} />
        <Route path={Routes.SUBSCRIPTION} element={<SubscriptionV2 />} />
        <Route path={Routes.OFFERS} element={<OffersV2 />} />
        <Route path={Routes.ACCESS_PRODUCT} element={<AccessProduct />} />
        <Route path={Routes.SIGN_IN} element={<SignInV2 />} />
        <Route path={Routes.QUIZ} element={<Quiz />} />
        <Route path={Routes.PHOTOLAB_QUIZ} element={<PhotolabQuiz />} />
        <Route path={Routes.ETHNICITY_QUIZ} element={<EthnicityQuiz />} />
        <Route path={Routes.COMPARISON_QUIZ} element={<ComparisonQuiz />} />
        <Route path={Routes.LOOKALIKE_QUIZ} element={<LookalikeQuiz />} />
        <Route
          path={Routes.LOOKALIKE_REPORT}
          element={<LookalikeReportHistoryWrap />}
        />
        <Route
          path={Routes.COMPARISION_REPORTS}
          element={<ComparisionReports />}
        />
        <Route path={Routes.LOOKALIKE_REPORTS} element={<LookalikeReports />} />
        <Route path={Routes.WEB_APP_PREVIEW} element={<WebAppPreview />} />
        <Route path={Routes.UNSUBSCRIBE} element={<Unsubscribe />} />
        <Route
          path={Routes.UNSUBSCRIBE_EMAIL}
          element={<UnsubscribeWithEmail />}
        />
        <Route path={Routes.ACCESS_RECOVERY} element={<AccessRecovery />} />
        <Route path={Routes.ACTIVATE} element={<SignInV2 isActivate />} />

        <Route path={Routes.WEBAPP} element={<WebApp />}>
          <Route
            index
            path={getLastPath(Routes.WEBAPP_MAIN)}
            element={<WebAppMainV2 />}
          />
          <Route
            index
            path={getLastPath(Routes.WEBAPP_PHOTOLAB)}
            element={<PhotoLab />}
          />
          <Route
            index
            path={getLastPath(Routes.WEBAPP_PHOTORECOVERY)}
            element={<PhotoRecovery />}
          />
          <Route
            index
            path={getLastPath(Routes.WEBAPP_PHOTORECOVERY_RESULTS)}
            element={<PhotoRecoveryResults />}
          />
          <Route
            index
            path={getLastPath(Routes.WEBAPP_PHOTOANIMATION)}
            element={<PhotoAnimation />}
          />
          <Route
            index
            path={getLastPath(Routes.WEBAPP_PHOTOANIMATION_RESULTS)}
            element={<PhotoAnimationResults />}
          />
          <Route
            path={getLastPath(Routes.WEBAPP_ETHNICITY) + '/:reportId'}
            element={<MyEthnicityWrap />}
          />
          <Route
            path={getLastPath(Routes.WEBAPP_PROFILE)}
            element={<Profile />}
          />
          <Route
            path={getLastPath(Routes.WEBAPP_REPORTS_HISTORY)}
            element={<ReportsHistory />}
          />
          <Route
            path={getLastPath(Routes.WEBAPP_FAMILY_TREE)}
            element={<FamilyTree />}
          />
          <Route
            path={getLastPath(Routes.WEBAPP_FACE_COMPARE_HISTORY)}
            element={<FaceMatchHistory />}
          />
          <Route
            path={getLastPath(Routes.WEBAPP_CELEBRITY_LOOKALIKE_HISTORY)}
            element={<CelebrityLookalikeHistory />}
          />

          <Route
            path={getLastPath(Routes.WEBAPP_GENERAL_REPORTS_HISTORY)}
            element={<GeneralReportsHistory />}
          />
          <Route
            path={getLastPath(Routes.WEBAPP_MANAGE_SUBSCRIPTIONS)}
            element={<ManageSubscriptionsV2 />}
          />
          <Route path={getLastPath(Routes.SHARE)} element={<SharePage />} />
          <Route
            path={getLastPath(Routes.SHARE_RECOVERY_RESULTS)}
            element={<SharePhotoRecoveryPage />}
          />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </RoutesInner>
    </Suspense>
  );
}
